import React, { useState, useEffect, useRef } from 'react';
import { WorkflowFinaliseReason } from '@Shared.Angular/@types/core/contracts/queryModel/flows/workflowFinaliseReason';
import { IFlowStatusReport } from '@Shared.Angular/@types/core/contracts/queryModel/reports/maintenance/flowStatusReport';
import { getService } from '@Client/runner.hooks/useService';
import ClipboardCopy from '@Shared.Angular/flowingly.components/ClipboardCopy/ClipboardCopy';

interface IFlowStatusReportRow {
  date: string;
  flowModelName: string;
  email: string;
  counts: IFlowStatusReportCounts;
}

interface IFlowStatusReportCounts {
  started: number;
  inProgress: number;
  withdrawn: number;
  completed: number;
  rejected: number;
}

type FlowStatusReportProps = {
  data: IFlowStatusReport;
};

export const FlowStatusReport = ({ data }: FlowStatusReportProps) => {
  const momentService = getService('momentService');
  const [reportRows, setReportRows] = useState<IFlowStatusReportRow[]>([]);
  const [clipboard, setClipboard] = useState<string>('');
  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (!data) {
      setReportRows([]);
      return;
    }

    const report = {};
    for (const flow of data.flows) {
      const utcDate = momentService.utc(flow.startDateUtc).format('YYYY-MM-DD');
      const dateReport = report[utcDate] || {};
      report[utcDate] = dateReport;
      const flowModelReport = dateReport[flow.flowModelName] || {};
      dateReport[flow.flowModelName] = flowModelReport;
      const userReport = flowModelReport[flow.startedByUserEmail] || {
        started: 0,
        inProgress: 0,
        withdrawn: 0,
        completed: 0,
        rejected: 0
      };
      flowModelReport[flow.startedByUserEmail] = userReport;
      userReport.started++;
      if (flow.finaliseReason === WorkflowFinaliseReason.Withdrawn) {
        userReport.withdrawn++;
      } else if (flow.finaliseReason === WorkflowFinaliseReason.Rejected) {
        userReport.rejected++;
      } else if (flow.finaliseReason === WorkflowFinaliseReason.Completed) {
        userReport.completed++;
      } else {
        userReport.inProgress++;
      }
    }

    const rows: IFlowStatusReportRow[] = [];
    for (const date in report) {
      for (const flowModelName in report[date]) {
        for (const email in report[date][flowModelName]) {
          rows.push({
            date: date,
            flowModelName: flowModelName,
            email: email,
            counts: report[date][flowModelName][email]
          });
        }
      }
    }

    rows.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.flowModelName !== b.flowModelName)
        return a.flowModelName.localeCompare(b.flowModelName);
      return a.email.localeCompare(b.email);
    });

    setReportRows(rows);

    setTimeout(() => {
      if (tableRef.current) {
        setClipboard(tableRef.current.outerHTML);
      }
    }, 100);
  }, [data]);

  return (
    <>
      <ClipboardCopy copyText={clipboard} />
      <table id="flow-status-report" ref={tableRef}>
        <thead>
          <tr>
            <th>Date Started (UTC)</th>
            <th>Flow Model</th>
            <th>Started by User</th>
            <th>Started</th>
            <th>In Progress</th>
            <th>Completed</th>
            <th>Withdrawn</th>
            <th>Rejected</th>
          </tr>
        </thead>
        <tbody>
          {reportRows.map((row, index) => (
            <tr key={index}>
              <td>{row.date}</td>
              <td>{row.flowModelName}</td>
              <td>{row.email}</td>
              <td>{row.counts.started}</td>
              <td>{row.counts.inProgress}</td>
              <td>{row.counts.completed}</td>
              <td>{row.counts.withdrawn}</td>
              <td>{row.counts.rejected}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};
